import { Candle } from '@starx/shared-types';

export interface IndicatorPoint {
  time: number;
  value: number | null;
}

export interface BollingerBandsPoint {
  time: number;
  upper: number | null;
  middle: number | null;
  lower: number | null;
}

export interface MACDPoint {
  time: number;
  macd: number | null;
  signal: number | null;
  histogram: number | null;
}

export interface StochasticPoint {
  time: number;
  k: number | null;
  d: number | null;
}

export interface SupertrendPoint {
  time: number;
  value: number | null;
  trend: 'UP' | 'DOWN' | null;
}

export interface IchimokuPoint {
  time: number;
  tenkan: number | null;
  kijun: number | null;
  senkouA: number | null;
  senkouB: number | null;
  chikou: number | null;
}

export interface AlligatorPoint {
  time: number;
  jaw: number | null;
  teeth: number | null;
  lips: number | null;
}

export interface DonchianPoint {
  time: number;
  upper: number | null;
  middle: number | null;
  lower: number | null;
}

export interface KeltnerPoint {
  time: number;
  upper: number | null;
  middle: number | null;
  lower: number | null;
}

export interface ADXPoint {
  time: number;
  adx: number | null;
  plusDI: number | null;
  minusDI: number | null;
}

export interface FractalPoint {
  time: number;
  up: number | null;
  down: number | null;
}

export interface EnvelopePoint {
  time: number;
  upper: number | null;
  middle: number | null;
  lower: number | null;
}

export interface AroonPoint {
  time: number;
  aroonUp: number | null;
  aroonDown: number | null;
  oscillator: number | null;
}

export interface VortexPoint {
  time: number;
  plusVI: number | null;
  minusVI: number | null;
}

export interface ElderRayPoint {
  time: number;
  bullsPower: number | null;
  bearsPower: number | null;
}

export interface ACPoint {
  time: number;
  value: number | null;
  isIncreasing: boolean;
}

export interface DeMarkerPoint {
  time: number;
  value: number | null;
}

// ============================================================================
// 1. TREND INDICATORS
// ============================================================================

/**
 * 📈 Simple Moving Average (SMA)
 */
export function calculateSMA(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sum += candles[j].close;
    }
    result.push({ time: candles[i].time, value: Number((sum / period).toFixed(5)) });
  }
  return result;
}

/**
 * 📈 Exponential Moving Average (EMA)
 */
export function calculateEMA(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  const k = 2 / (period + 1);
  let prevEMA: number | null = null;

  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    if (prevEMA === null) {
      let sum = 0;
      for (let j = 0; j < period; j++) {
        sum += candles[j].close;
      }
      prevEMA = sum / period;
      result.push({ time: candles[i].time, value: Number(prevEMA.toFixed(5)) });
    } else {
      const currentEMA: number = candles[i].close * k + (prevEMA !== null ? prevEMA : candles[i].close) * (1 - k);
      prevEMA = currentEMA;
      result.push({ time: candles[i].time, value: Number(currentEMA.toFixed(5)) });
    }
  }
  return result;
}

/**
 * 📈 Weighted Moving Average (WMA)
 */
export function calculateWMA(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  const weightSum = (period * (period + 1)) / 2;

  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    let sum = 0;
    for (let j = 0; j < period; j++) {
      const weight = j + 1;
      sum += candles[i - period + 1 + j].close * weight;
    }
    result.push({ time: candles[i].time, value: Number((sum / weightSum).toFixed(5)) });
  }
  return result;
}

/**
 * 📈 Hull Moving Average (HMA)
 * HMA = WMA(2*WMA(n/2) - WMA(n)), sqrt(n))
 */
export function calculateHullMA(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const halfPeriod = Math.max(1, Math.floor(period / 2));
  const sqrtPeriod = Math.max(1, Math.floor(Math.sqrt(period)));

  const wmaHalf = calculateWMA(candles, halfPeriod);
  const wmaFull = calculateWMA(candles, period);

  const rawDiffCandles: Candle[] = [];
  for (let i = 0; i < candles.length; i++) {
    const halfVal = wmaHalf[i]?.value;
    const fullVal = wmaFull[i]?.value;
    if (halfVal !== null && fullVal !== null) {
      const diff = 2 * halfVal - fullVal;
      rawDiffCandles.push({
        time: candles[i].time,
        open: diff,
        high: diff,
        low: diff,
        close: diff,
        volume: candles[i].volume
      });
    } else {
      rawDiffCandles.push({
        time: candles[i].time,
        open: 0,
        high: 0,
        low: 0,
        close: 0,
        volume: 0
      });
    }
  }

  const hmaResult = calculateWMA(rawDiffCandles, sqrtPeriod);
  const result: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period + sqrtPeriod - 2) {
      result.push({ time: candles[i].time, value: null });
    } else {
      result.push({ time: candles[i].time, value: hmaResult[i]?.value ?? null });
    }
  }
  return result;
}

/**
 * 📈 Supertrend Indicator
 */
export function calculateSupertrend(candles: Candle[], period: number = 10, multiplier: number = 3): SupertrendPoint[] {
  const atr = calculateATR(candles, period);
  const result: SupertrendPoint[] = [];

  let upperBand = 0;
  let lowerBand = 0;
  let trend: 'UP' | 'DOWN' = 'UP';

  for (let i = 0; i < candles.length; i++) {
    const atrVal = atr[i]?.value;
    if (atrVal === null || i < period) {
      result.push({ time: candles[i].time, value: null, trend: null });
      continue;
    }

    const hl2 = (candles[i].high + candles[i].low) / 2;
    const basicUpper = hl2 + multiplier * atrVal;
    const basicLower = hl2 - multiplier * atrVal;

    if (i === period) {
      upperBand = basicUpper;
      lowerBand = basicLower;
      trend = candles[i].close > upperBand ? 'UP' : 'DOWN';
    } else {
      const prevClose = candles[i - 1].close;

      // Lower band ratchets up during uptrend
      if (basicLower > lowerBand || prevClose < lowerBand) {
        lowerBand = basicLower;
      }

      // Upper band ratchets down during downtrend
      if (basicUpper < upperBand || prevClose > upperBand) {
        upperBand = basicUpper;
      }

      if (trend === 'UP' && candles[i].close < lowerBand) {
        trend = 'DOWN';
        upperBand = basicUpper;
      } else if (trend === 'DOWN' && candles[i].close > upperBand) {
        trend = 'UP';
        lowerBand = basicLower;
      }
    }

    const value = trend === 'UP' ? Number(lowerBand.toFixed(5)) : Number(upperBand.toFixed(5));
    result.push({ time: candles[i].time, value, trend });
  }

  return result;
}

/**
 * 📈 Ichimoku Kinko Hyo
 */
export function calculateIchimoku(candles: Candle[], tenkanPeriod: number = 9, kijunPeriod: number = 26, senkouPeriod: number = 52): IchimokuPoint[] {
  const getHighLowMid = (endIdx: number, p: number): number | null => {
    if (endIdx < p - 1) return null;
    let high = -Infinity;
    let low = Infinity;
    for (let i = endIdx - p + 1; i <= endIdx; i++) {
      if (candles[i].high > high) high = candles[i].high;
      if (candles[i].low < low) low = candles[i].low;
    }
    return (high + low) / 2;
  };

  const result: IchimokuPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    const tenkan = getHighLowMid(i, tenkanPeriod);
    const kijun = getHighLowMid(i, kijunPeriod);
    let senkouA: number | null = null;
    if (tenkan !== null && kijun !== null) {
      senkouA = Number(((tenkan + kijun) / 2).toFixed(5));
    }
    const senkouB = getHighLowMid(i, senkouPeriod);
    const chikou = i + 26 < candles.length ? candles[i + 26].close : null;

    result.push({
      time: candles[i].time,
      tenkan: tenkan !== null ? Number(tenkan.toFixed(5)) : null,
      kijun: kijun !== null ? Number(kijun.toFixed(5)) : null,
      senkouA,
      senkouB: senkouB !== null ? Number(senkouB.toFixed(5)) : null,
      chikou: chikou !== null ? Number(chikou.toFixed(5)) : null
    });
  }
  return result;
}

/**
 * 📈 Parabolic SAR
 */
export function calculateParabolicSAR(candles: Candle[], step: number = 0.02, maxStep: number = 0.2): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  if (candles.length < 2) {
    return candles.map(c => ({ time: c.time, value: null }));
  }

  let isLong = candles[1].close > candles[0].close;
  let af = step;
  let ep = isLong ? candles[0].high : candles[0].low;
  let sar = isLong ? candles[0].low : candles[0].high;

  result.push({ time: candles[0].time, value: null });

  for (let i = 1; i < candles.length; i++) {
    sar = sar + af * (ep - sar);
    if (isLong) {
      if (candles[i].low < sar) {
        isLong = false;
        sar = ep;
        af = step;
        ep = candles[i].low;
      } else {
        if (candles[i].high > ep) {
          ep = candles[i].high;
          af = Math.min(af + step, maxStep);
        }
      }
    } else {
      if (candles[i].high > sar) {
        isLong = true;
        sar = ep;
        af = step;
        ep = candles[i].high;
      } else {
        if (candles[i].low < ep) {
          ep = candles[i].low;
          af = Math.min(af + step, maxStep);
        }
      }
    }
    result.push({ time: candles[i].time, value: Number(sar.toFixed(5)) });
  }

  return result;
}

/**
 * 📈 Bill Williams Alligator
 */
export function calculateAlligator(candles: Candle[]): AlligatorPoint[] {
  const smma = (period: number): (number | null)[] => {
    const res: (number | null)[] = [];
    let prev: number | null = null;
    for (let i = 0; i < candles.length; i++) {
      const median = (candles[i].high + candles[i].low) / 2;
      if (i < period - 1) {
        res.push(null);
        continue;
      }
      if (prev === null) {
        let sum = 0;
        for (let j = 0; j < period; j++) sum += (candles[j].high + candles[j].low) / 2;
        prev = sum / period;
      } else {
        prev = (prev * (period - 1) + median) / period;
      }
      res.push(prev);
    }
    return res;
  };

  const jawSMMA = smma(13);
  const teethSMMA = smma(8);
  const lipsSMMA = smma(5);

  const result: AlligatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    result.push({
      time: candles[i].time,
      jaw: jawSMMA[i] !== null ? Number(jawSMMA[i]!.toFixed(5)) : null,
      teeth: teethSMMA[i] !== null ? Number(teethSMMA[i]!.toFixed(5)) : null,
      lips: lipsSMMA[i] !== null ? Number(lipsSMMA[i]!.toFixed(5)) : null
    });
  }
  return result;
}

/**
 * 📈 ZigZag Indicator (Institutional Depth/Deviation/Backstep swing pivot model)
 */
export function calculateZigZag(
  candles: Candle[],
  depth: number = 10,
  deviationPercent: number = 0.03,
  backstep: number = 3,
): IndicatorPoint[] {
  const n = candles.length;
  if (n === 0) return [];
  if (n < 4) return candles.map((c) => ({ time: c.time, value: c.close }));

  // Adaptive depth based on available history so it always renders cleanly even on short 1m history
  const effectiveDepth = Math.max(3, Math.min(depth, Math.floor(n / 6)));
  const effectiveBackstep = Math.max(2, Math.min(backstep, Math.floor(effectiveDepth / 2)));

  // Measure average bar range to set realistic minimum deviation across Forex, Crypto, and Stocks
  let totalRange = 0;
  for (let i = 0; i < n; i++) totalRange += candles[i].high - candles[i].low;
  const avgRange = totalRange / n || 0.0001;
  const avgPrice = candles[n - 1]?.close || 1;
  const minDevPrice = Math.max(avgRange * 0.7, avgPrice * (deviationPercent / 100));

  // Step 1: Scan for local peaks (highs) and valleys (lows)
  const highs: (number | null)[] = new Array(n).fill(null);
  const lows: (number | null)[] = new Array(n).fill(null);

  for (let i = effectiveDepth; i < n; i++) {
    let isHigh = true;
    let isLow = true;
    const curH = candles[i].high;
    const curL = candles[i].low;

    for (let k = 1; k <= effectiveDepth; k++) {
      if (candles[i - k].high > curH) isHigh = false;
      if (candles[i - k].low < curL) isLow = false;
    }

    if (isHigh) highs[i] = curH;
    if (isLow) lows[i] = curL;
  }

  // Step 2: Build alternating sequence of pivots with backstep and deviation filtering
  interface Pivot {
    index: number;
    price: number;
    type: "HIGH" | "LOW";
  }
  const pivots: Pivot[] = [];

  for (let i = 0; i < n; i++) {
    const h = highs[i];
    const l = lows[i];
    if (h === null && l === null) continue;

    if (h !== null && l === null) {
      if (pivots.length === 0) {
        pivots.push({ index: i, price: h, type: "HIGH" });
      } else {
        const last = pivots[pivots.length - 1];
        if (last.type === "HIGH") {
          if (h > last.price) {
            pivots[pivots.length - 1] = { index: i, price: h, type: "HIGH" };
          }
        } else {
          if (i - last.index >= effectiveBackstep && h - last.price >= minDevPrice) {
            pivots.push({ index: i, price: h, type: "HIGH" });
          }
        }
      }
    } else if (l !== null && h === null) {
      if (pivots.length === 0) {
        pivots.push({ index: i, price: l, type: "LOW" });
      } else {
        const last = pivots[pivots.length - 1];
        if (last.type === "LOW") {
          if (l < last.price) {
            pivots[pivots.length - 1] = { index: i, price: l, type: "LOW" };
          }
        } else {
          if (i - last.index >= effectiveBackstep && last.price - l >= minDevPrice) {
            pivots.push({ index: i, price: l, type: "LOW" });
          }
        }
      }
    } else if (h !== null && l !== null) {
      if (pivots.length === 0) {
        pivots.push({ index: i, price: h, type: "HIGH" });
      } else {
        const last = pivots[pivots.length - 1];
        if (last.type === "HIGH") {
          if (i - last.index >= effectiveBackstep && last.price - l >= minDevPrice) {
            pivots.push({ index: i, price: l, type: "LOW" });
          }
        } else {
          if (i - last.index >= effectiveBackstep && h - last.price >= minDevPrice) {
            pivots.push({ index: i, price: h, type: "HIGH" });
          }
        }
      }
    }
  }

  // If fewer than 2 pivots were found, connect extreme low and high
  if (pivots.length < 2) {
    let minIdx = 0;
    let maxIdx = 0;
    for (let i = 1; i < n; i++) {
      if (candles[i].low < candles[minIdx].low) minIdx = i;
      if (candles[i].high > candles[maxIdx].high) maxIdx = i;
    }
    if (minIdx !== maxIdx) {
      pivots.length = 0;
      if (minIdx < maxIdx) {
        pivots.push({ index: minIdx, price: candles[minIdx].low, type: "LOW" });
        pivots.push({ index: maxIdx, price: candles[maxIdx].high, type: "HIGH" });
      } else {
        pivots.push({ index: maxIdx, price: candles[maxIdx].high, type: "HIGH" });
        pivots.push({ index: minIdx, price: candles[minIdx].low, type: "LOW" });
      }
    }
  }

  // Step 3: Interpolate values along the straight line segments connecting the pivots
  const result: IndicatorPoint[] = candles.map((c) => ({ time: c.time, value: null }));
  if (pivots.length === 0) return result;

  for (let p = 0; p < pivots.length - 1; p++) {
    const p1 = pivots[p];
    const p2 = pivots[p + 1];
    const span = p2.index - p1.index;
    for (let idx = p1.index; idx <= p2.index; idx++) {
      const frac = span === 0 ? 0 : (idx - p1.index) / span;
      result[idx].value = Number((p1.price + frac * (p2.price - p1.price)).toFixed(6));
    }
  }

  // From last pivot to latest bar: draw active provisional leg
  const lastP = pivots[pivots.length - 1];
  if (lastP.index < n - 1) {
    const span = n - 1 - lastP.index;
    const endPrice = candles[n - 1].close;
    for (let idx = lastP.index; idx < n; idx++) {
      const frac = span === 0 ? 0 : (idx - lastP.index) / span;
      result[idx].value = Number((lastP.price + frac * (endPrice - lastP.price)).toFixed(6));
    }
  }

  return result;
}

// ============================================================================
// 2. OSCILLATORS
// ============================================================================

/**
 * 📊 Relative Strength Index (RSI) - Wilder's Smoothed
 */
export function calculateRSI(candles: Candle[], period: number = 14): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  if (candles.length <= period) {
    return candles.map(c => ({ time: c.time, value: null }));
  }

  let avgGain = 0;
  let avgLoss = 0;

  for (let i = 1; i <= period; i++) {
    const change = candles[i].close - candles[i - 1].close;
    if (change > 0) avgGain += change;
    else avgLoss += Math.abs(change);
  }

  avgGain /= period;
  avgLoss /= period;

  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }

    if (i > period) {
      const change = candles[i].close - candles[i - 1].close;
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? Math.abs(change) : 0;
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
    }

    if (avgLoss === 0) {
      result.push({ time: candles[i].time, value: 100 });
    } else {
      const rs = avgGain / avgLoss;
      const rsi = 100 - (100 / (1 + rs));
      result.push({ time: candles[i].time, value: Number(rsi.toFixed(2)) });
    }
  }

  return result;
}

/**
 * 📊 Stochastic Oscillator
 */
export function calculateStochastic(candles: Candle[], kPeriod: number = 14, dPeriod: number = 3): StochasticPoint[] {
  const rawK: (number | null)[] = [];

  for (let i = 0; i < candles.length; i++) {
    if (i < kPeriod - 1) {
      rawK.push(null);
      continue;
    }
    let highest = -Infinity;
    let lowest = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (candles[j].high > highest) highest = candles[j].high;
      if (candles[j].low < lowest) lowest = candles[j].low;
    }
    const currentClose = candles[i].close;
    const k = highest === lowest ? 50 : ((currentClose - lowest) / (highest - lowest)) * 100;
    rawK.push(k);
  }

  const result: StochasticPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    const kVal = rawK[i];
    if (kVal === null || i < kPeriod + dPeriod - 2) {
      result.push({ time: candles[i].time, k: kVal !== null ? Number(kVal.toFixed(2)) : null, d: null });
      continue;
    }
    let dSum = 0;
    let validCount = 0;
    for (let j = i - dPeriod + 1; j <= i; j++) {
      if (rawK[j] !== null) {
        dSum += rawK[j]!;
        validCount++;
      }
    }
    const dVal = validCount === dPeriod ? dSum / dPeriod : null;
    result.push({
      time: candles[i].time,
      k: Number(kVal.toFixed(2)),
      d: dVal !== null ? Number(dVal.toFixed(2)) : null
    });
  }

  return result;
}

/**
 * 📊 Moving Average Convergence Divergence (MACD)
 */
export function calculateMACD(candles: Candle[], fastPeriod: number = 12, slowPeriod: number = 26, signalPeriod: number = 9): MACDPoint[] {
  const fastEMA = calculateEMA(candles, fastPeriod);
  const slowEMA = calculateEMA(candles, slowPeriod);

  const macdLine: (number | null)[] = [];
  for (let i = 0; i < candles.length; i++) {
    const f = fastEMA[i]?.value;
    const s = slowEMA[i]?.value;
    if (f !== null && s !== null) {
      macdLine.push(f - s);
    } else {
      macdLine.push(null);
    }
  }

  const kSignal = 2 / (signalPeriod + 1);
  const signalLine: (number | null)[] = [];
  let prevSignal: number | null = null;

  for (let i = 0; i < macdLine.length; i++) {
    const m = macdLine[i];
    if (m === null) {
      signalLine.push(null);
      continue;
    }
    if (prevSignal === null) {
      let sum = 0;
      let count = 0;
      for (let j = Math.max(0, i - signalPeriod + 1); j <= i; j++) {
        if (macdLine[j] !== null) {
          sum += macdLine[j]!;
          count++;
        }
      }
      if (count === signalPeriod) {
        prevSignal = sum / signalPeriod;
        signalLine.push(prevSignal);
      } else {
        signalLine.push(null);
      }
    } else {
      prevSignal = m * kSignal + prevSignal * (1 - kSignal);
      signalLine.push(prevSignal);
    }
  }

  const result: MACDPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    const m = macdLine[i];
    const s = signalLine[i];
    const hist = m !== null && s !== null ? m - s : null;
    result.push({
      time: candles[i].time,
      macd: m !== null ? Number(m.toFixed(5)) : null,
      signal: s !== null ? Number(s.toFixed(5)) : null,
      histogram: hist !== null ? Number(hist.toFixed(5)) : null
    });
  }

  return result;
}

/**
 * 📊 Commodity Channel Index (CCI)
 */
export function calculateCCI(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  const tpList = candles.map(c => (c.high + c.low + c.close) / 3);

  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += tpList[j];
    const smaTP = sum / period;

    let meanDevSum = 0;
    for (let j = i - period + 1; j <= i; j++) meanDevSum += Math.abs(tpList[j] - smaTP);
    const meanDev = meanDevSum / period;

    const cci = meanDev === 0 ? 0 : (tpList[i] - smaTP) / (0.015 * meanDev);
    result.push({ time: candles[i].time, value: Number(cci.toFixed(2)) });
  }

  return result;
}

/**
 * 📊 Williams %R
 */
export function calculateWilliamsR(candles: Candle[], period: number = 14): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    let highest = -Infinity;
    let lowest = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      if (candles[j].high > highest) highest = candles[j].high;
      if (candles[j].low < lowest) lowest = candles[j].low;
    }
    const r = highest === lowest ? -50 : ((highest - candles[i].close) / (highest - lowest)) * -100;
    result.push({ time: candles[i].time, value: Number(r.toFixed(2)) });
  }
  return result;
}

/**
 * 📊 Awesome Oscillator (AO)
 */
export function calculateAwesomeOscillator(candles: Candle[]): IndicatorPoint[] {
  const medianCandles = candles.map(c => ({
    ...c,
    close: (c.high + c.low) / 2
  }));
  const sma5 = calculateSMA(medianCandles, 5);
  const sma34 = calculateSMA(medianCandles, 34);

  const result: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    const val5 = sma5[i]?.value;
    const val34 = sma34[i]?.value;
    if (val5 !== null && val34 !== null) {
      result.push({ time: candles[i].time, value: Number((val5 - val34).toFixed(5)) });
    } else {
      result.push({ time: candles[i].time, value: null });
    }
  }
  return result;
}

/**
/**
 * 📊 Momentum (MOM) - Institutional 100-baseline oscillator: (Close / Close[n]) * 100
 */
export function calculateMomentum(candles: Candle[], period: number = 10): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      result.push({ time: candles[i].time, value: null });
    } else {
      const prev = candles[i - period].close;
      const mom = prev > 0 ? (candles[i].close / prev) * 100 : 100;
      result.push({ time: candles[i].time, value: Number(mom.toFixed(2)) });
    }
  }
  return result;
}

/**
 * 📊 Rate of Change (ROC)
 */
export function calculateROC(candles: Candle[], period: number = 12): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      result.push({ time: candles[i].time, value: null });
    } else {
      const prev = candles[i - period].close;
      const roc = prev !== 0 ? ((candles[i].close - prev) / prev) * 100 : 0;
      result.push({ time: candles[i].time, value: Number(roc.toFixed(2)) });
    }
  }
  return result;
}

// ============================================================================
// 3. VOLATILITY INDICATORS
// ============================================================================

/**
 * 🌊 Bollinger Bands
 */
export function calculateBollingerBands(candles: Candle[], period: number = 20, multiplier: number = 2): BollingerBandsPoint[] {
  const smaPoints = calculateSMA(candles, period);
  const result: BollingerBandsPoint[] = [];

  for (let i = 0; i < candles.length; i++) {
    const smaVal = smaPoints[i]?.value;
    if (smaVal === null || i < period - 1) {
      result.push({ time: candles[i].time, upper: null, middle: null, lower: null });
      continue;
    }

    let varianceSum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const diff = candles[j].close - smaVal;
      varianceSum += diff * diff;
    }
    const stdDev = Math.sqrt(varianceSum / period);

    result.push({
      time: candles[i].time,
      upper: Number((smaVal + multiplier * stdDev).toFixed(5)),
      middle: Number(smaVal.toFixed(5)),
      lower: Number((smaVal - multiplier * stdDev).toFixed(5))
    });
  }

  return result;
}

/**
 * 🌊 Average True Range (ATR)
 */
export function calculateATR(candles: Candle[], period: number = 14): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  if (candles.length === 0) return result;

  const trList: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const prevC = candles[i - 1].close;
    const tr = Math.max(h - l, Math.abs(h - prevC), Math.abs(l - prevC));
    trList.push(tr);
  }

  let prevATR: number | null = null;
  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    if (prevATR === null) {
      let sum = 0;
      for (let j = 0; j < period; j++) sum += trList[j];
      prevATR = sum / period;
    } else {
      prevATR = (prevATR * (period - 1) + trList[i]) / period;
    }
    result.push({ time: candles[i].time, value: Number(prevATR.toFixed(7)) });
  }

  return result;
}

/**
 * 🌊 Donchian Channels
 */
export function calculateDonchianChannels(candles: Candle[], period: number = 20): DonchianPoint[] {
  const result: DonchianPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, upper: null, middle: null, lower: null });
      continue;
    }
    let highest = -Infinity;
    let lowest = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      if (candles[j].high > highest) highest = candles[j].high;
      if (candles[j].low < lowest) lowest = candles[j].low;
    }
    const mid = (highest + lowest) / 2;
    result.push({
      time: candles[i].time,
      upper: Number(highest.toFixed(5)),
      middle: Number(mid.toFixed(5)),
      lower: Number(lowest.toFixed(5))
    });
  }
  return result;
}

/**
 * 🌊 Keltner Channels
 */
export function calculateKeltnerChannels(candles: Candle[], emaPeriod: number = 20, atrPeriod: number = 10, multiplier: number = 2): KeltnerPoint[] {
  const ema = calculateEMA(candles, emaPeriod);
  const atr = calculateATR(candles, atrPeriod);

  const result: KeltnerPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    const emaVal = ema[i]?.value;
    const atrVal = atr[i]?.value;
    if (emaVal !== null && atrVal !== null) {
      result.push({
        time: candles[i].time,
        upper: Number((emaVal + multiplier * atrVal).toFixed(5)),
        middle: Number(emaVal.toFixed(5)),
        lower: Number((emaVal - multiplier * atrVal).toFixed(5))
      });
    } else {
      result.push({ time: candles[i].time, upper: null, middle: null, lower: null });
    }
  }
  return result;
}

/**
 * 🌊 Standard Deviation (Volatility)
 */
export function calculateStdDev(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const sma = calculateSMA(candles, period);
  const result: IndicatorPoint[] = [];

  for (let i = 0; i < candles.length; i++) {
    const mean = sma[i]?.value;
    if (mean === null) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    let sumSq = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const d = candles[j].close - mean;
      sumSq += d * d;
    }
    result.push({ time: candles[i].time, value: Number(Math.sqrt(sumSq / period).toFixed(5)) });
  }
  return result;
}

// ============================================================================
// 4. VOLUME INDICATORS
// ============================================================================

/**
 * 📊 Volume Weighted Average Price (VWAP)
 */
export function calculateVWAP(candles: Candle[]): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  let cumVol = 0;
  let cumTPVol = 0;

  for (let i = 0; i < candles.length; i++) {
    const tp = (candles[i].high + candles[i].low + candles[i].close) / 3;
    const vol = candles[i].volume > 0 ? candles[i].volume : 1;
    cumVol += vol;
    cumTPVol += tp * vol;

    const vwap = cumTPVol / cumVol;
    result.push({ time: candles[i].time, value: Number(vwap.toFixed(5)) });
  }

  return result;
}

/**
 * 📊 On-Balance Volume (OBV)
 */
export function calculateOBV(candles: Candle[]): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  if (candles.length === 0) return result;

  let obv = 0;
  result.push({ time: candles[0].time, value: obv });

  for (let i = 1; i < candles.length; i++) {
    const prevC = candles[i - 1].close;
    const curC = candles[i].close;
    const rawVol = candles[i].volume;
    const vol = typeof rawVol === "number" && Number.isFinite(rawVol) && rawVol > 0 ? rawVol : 100;

    if (curC > prevC) obv += vol;
    else if (curC < prevC) obv -= vol;

    result.push({ time: candles[i].time, value: obv });
  }

  return result;
}

/**
 * 📊 Chaikin Money Flow (CMF)
 */
export function calculateCMF(candles: Candle[], period: number = 20): IndicatorPoint[] {
  const result: IndicatorPoint[] = [];
  const mfvList: number[] = [];

  for (let i = 0; i < candles.length; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const c = candles[i].close;
    const rawVol = candles[i].volume;
    const v = typeof rawVol === "number" && Number.isFinite(rawVol) && rawVol > 0 ? rawVol : 100;
    const mfm = h === l ? 0 : ((c - l) - (h - c)) / (h - l);
    mfvList.push(mfm * v);
  }

  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }
    let sumMFV = 0;
    let sumVol = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sumMFV += mfvList[j];
      const rawVol = candles[j].volume;
      const v = typeof rawVol === "number" && Number.isFinite(rawVol) && rawVol > 0 ? rawVol : 100;
      sumVol += v;
    }
    const cmf = sumVol === 0 ? 0 : sumMFV / sumVol;
    result.push({ time: candles[i].time, value: Number(cmf.toFixed(4)) });
  }

  return result;
}

/**
 * 📊 Average Directional Index (ADX, +DI, -DI)
 */
export function calculateADX(candles: Candle[], period: number = 14): ADXPoint[] {
  const result: ADXPoint[] = [];
  if (candles.length === 0) return result;

  const tr: number[] = [candles[0].high - candles[0].low];
  const plusDM: number[] = [0];
  const minusDM: number[] = [0];

  for (let i = 1; i < candles.length; i++) {
    const curH = candles[i].high;
    const curL = candles[i].low;
    const prevH = candles[i - 1].high;
    const prevL = candles[i - 1].low;
    const prevC = candles[i - 1].close;

    const currentTR = Math.max(curH - curL, Math.abs(curH - prevC), Math.abs(curL - prevC));
    tr.push(currentTR);

    const upMove = curH - prevH;
    const downMove = prevL - curL;

    if (upMove > downMove && upMove > 0) {
      plusDM.push(upMove);
    } else {
      plusDM.push(0);
    }

    if (downMove > upMove && downMove > 0) {
      minusDM.push(downMove);
    } else {
      minusDM.push(0);
    }
  }

  let smoothedTR = 0;
  let smoothedPlusDM = 0;
  let smoothedMinusDM = 0;

  const dxValues: (number | null)[] = [];
  const plusDIValues: (number | null)[] = [];
  const minusDIValues: (number | null)[] = [];

  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      smoothedTR += tr[i];
      smoothedPlusDM += plusDM[i];
      smoothedMinusDM += minusDM[i];
      dxValues.push(null);
      plusDIValues.push(null);
      minusDIValues.push(null);
      continue;
    }

    if (i > period) {
      smoothedTR = smoothedTR - (smoothedTR / period) + tr[i];
      smoothedPlusDM = smoothedPlusDM - (smoothedPlusDM / period) + plusDM[i];
      smoothedMinusDM = smoothedMinusDM - (smoothedMinusDM / period) + minusDM[i];
    }

    const pDI = smoothedTR === 0 ? 0 : (smoothedPlusDM / smoothedTR) * 100;
    const mDI = smoothedTR === 0 ? 0 : (smoothedMinusDM / smoothedTR) * 100;
    const diSum = pDI + mDI;
    const dx = diSum === 0 ? 0 : (Math.abs(pDI - mDI) / diSum) * 100;

    plusDIValues.push(Number(pDI.toFixed(2)));
    minusDIValues.push(Number(mDI.toFixed(2)));
    dxValues.push(dx);
  }

  let smoothedDX = 0;
  let dxCount = 0;

  for (let i = 0; i < candles.length; i++) {
    const dx = dxValues[i];
    if (dx === null) {
      result.push({ time: candles[i].time, adx: null, plusDI: null, minusDI: null });
      continue;
    }

    dxCount++;
    if (dxCount < period) {
      smoothedDX += dx;
      result.push({
        time: candles[i].time,
        adx: null,
        plusDI: plusDIValues[i],
        minusDI: minusDIValues[i]
      });
      continue;
    }

    if (dxCount === period) {
      smoothedDX = (smoothedDX + dx) / period;
    } else {
      smoothedDX = (smoothedDX * (period - 1) + dx) / period;
    }

    result.push({
      time: candles[i].time,
      adx: Number(smoothedDX.toFixed(2)),
      plusDI: plusDIValues[i],
      minusDI: minusDIValues[i]
    });
  }

  return result;
}

/**
 * 📈 Bill Williams Fractals (5-bar swing high/low)
 */
export function calculateFractals(candles: Candle[]): FractalPoint[] {
  const result: FractalPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    result.push({ time: candles[i].time, up: null, down: null });
  }

  for (let i = 4; i < candles.length; i++) {
    const center = i - 2;
    const cHigh = candles[center].high;
    const cLow = candles[center].low;

    if (
      cHigh > candles[center - 2].high &&
      cHigh > candles[center - 1].high &&
      cHigh > candles[center + 1].high &&
      cHigh > candles[center + 2].high
    ) {
      result[center].up = cHigh;
    }

    if (
      cLow < candles[center - 2].low &&
      cLow < candles[center - 1].low &&
      cLow < candles[center + 1].low &&
      cLow < candles[center + 2].low
    ) {
      result[center].down = cLow;
    }
  }

  return result;
}

/**
 * 📈 Moving Average Envelopes
 */
export function calculateEnvelopes(candles: Candle[], period: number = 20, percent: number = 0.1): EnvelopePoint[] {
  const sma = calculateSMA(candles, period);
  const result: EnvelopePoint[] = [];

  for (let i = 0; i < candles.length; i++) {
    const mid = sma[i].value;
    if (mid === null) {
      result.push({ time: candles[i].time, upper: null, middle: null, lower: null });
    } else {
      const shift = mid * (percent / 100);
      result.push({
        time: candles[i].time,
        upper: Number((mid + shift).toFixed(5)),
        middle: mid,
        lower: Number((mid - shift).toFixed(5)),
      });
    }
  }

  return result;
}

/**
 * 📊 Aroon & Aroon Oscillator
 */
export function calculateAroon(candles: Candle[], period: number = 14): AroonPoint[] {
  const result: AroonPoint[] = [];

  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      result.push({ time: candles[i].time, aroonUp: null, aroonDown: null, oscillator: null });
      continue;
    }

    let highestIdx = i - period;
    let lowestIdx = i - period;
    let highestVal = candles[highestIdx].high;
    let lowestVal = candles[lowestIdx].low;

    for (let j = i - period; j <= i; j++) {
      if (candles[j].high >= highestVal) {
        highestVal = candles[j].high;
        highestIdx = j;
      }
      if (candles[j].low <= lowestVal) {
        lowestVal = candles[j].low;
        lowestIdx = j;
      }
    }

    const barsSinceHigh = i - highestIdx;
    const barsSinceLow = i - lowestIdx;

    const aroonUp = Number((((period - barsSinceHigh) / period) * 100).toFixed(2));
    const aroonDown = Number((((period - barsSinceLow) / period) * 100).toFixed(2));
    const osc = Number((aroonUp - aroonDown).toFixed(2));

    result.push({
      time: candles[i].time,
      aroonUp,
      aroonDown,
      oscillator: osc,
    });
  }

  return result;
}

/**
 * 📊 Vortex Indicator (VI+, VI-)
 */
export function calculateVortex(candles: Candle[], period: number = 14): VortexPoint[] {
  const result: VortexPoint[] = [];
  if (candles.length < 2) return result;

  const vmPlus: number[] = [0];
  const vmMinus: number[] = [0];
  const tr: number[] = [candles[0].high - candles[0].low];

  for (let i = 1; i < candles.length; i++) {
    const curH = candles[i].high;
    const curL = candles[i].low;
    const prevH = candles[i - 1].high;
    const prevL = candles[i - 1].low;
    const prevC = candles[i - 1].close;

    vmPlus.push(Math.abs(curH - prevL));
    vmMinus.push(Math.abs(curL - prevH));
    tr.push(Math.max(curH - curL, Math.abs(curH - prevC), Math.abs(curL - prevC)));
  }

  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      result.push({ time: candles[i].time, plusVI: null, minusVI: null });
      continue;
    }

    let sumVMPlus = 0;
    let sumVMMinus = 0;
    let sumTR = 0;

    for (let j = i - period + 1; j <= i; j++) {
      sumVMPlus += vmPlus[j];
      sumVMMinus += vmMinus[j];
      sumTR += tr[j];
    }

    const viPlus = sumTR === 0 ? 1 : sumVMPlus / sumTR;
    const viMinus = sumTR === 0 ? 1 : sumVMMinus / sumTR;

    result.push({
      time: candles[i].time,
      plusVI: Number(viPlus.toFixed(4)),
      minusVI: Number(viMinus.toFixed(4)),
    });
  }

  return result;
}

/**
 * 📊 Elder-Ray Index (Bulls Power & Bears Power)
 */
export function calculateElderRay(candles: Candle[], period: number = 13): ElderRayPoint[] {
  const ema = calculateEMA(candles, period);
  const result: ElderRayPoint[] = [];

  for (let i = 0; i < candles.length; i++) {
    const e = ema[i]?.value;
    if (e === null) {
      result.push({ time: candles[i].time, bullsPower: null, bearsPower: null });
    } else {
      result.push({
        time: candles[i].time,
        bullsPower: Number((candles[i].high - e).toFixed(5)),
        bearsPower: Number((candles[i].low - e).toFixed(5)),
      });
    }
  }

  return result;
}

/**
 * 📊 Accelerator Oscillator (AC)
 */
export function calculateAcceleratorOscillator(candles: Candle[]): ACPoint[] {
  const result: ACPoint[] = [];
  const aoPoints = calculateAwesomeOscillator(candles);

  const acValues: (number | null)[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < 4 || aoPoints[i].value === null) {
      acValues.push(null);
      continue;
    }

    let sum = 0;
    let count = 0;
    for (let j = i - 4; j <= i; j++) {
      if (aoPoints[j].value !== null) {
        sum += aoPoints[j].value!;
        count++;
      }
    }

    if (count === 5) {
      acValues.push(Number((aoPoints[i].value! - sum / 5).toFixed(5)));
    } else {
      acValues.push(null);
    }
  }

  for (let i = 0; i < candles.length; i++) {
    const cur = acValues[i];
    const prev = i > 0 ? acValues[i - 1] : null;
    const isIncreasing = cur !== null && prev !== null ? cur >= prev : true;

    result.push({
      time: candles[i].time,
      value: cur,
      isIncreasing,
    });
  }

  return result;
}

/**
 * 📊 DeMarker Indicator (DeM)
 */
export function calculateDeMarker(candles: Candle[], period: number = 14): DeMarkerPoint[] {
  const result: DeMarkerPoint[] = [];
  if (candles.length === 0) return result;

  const deMax: number[] = [0];
  const deMin: number[] = [0];

  for (let i = 1; i < candles.length; i++) {
    const highDiff = candles[i].high - candles[i - 1].high;
    const lowDiff = candles[i - 1].low - candles[i].low;

    deMax.push(highDiff > 0 ? highDiff : 0);
    deMin.push(lowDiff > 0 ? lowDiff : 0);
  }

  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      result.push({ time: candles[i].time, value: null });
      continue;
    }

    let sumMax = 0;
    let sumMin = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sumMax += deMax[j];
      sumMin += deMin[j];
    }

    const denom = sumMax + sumMin;
    const dem = denom === 0 ? 0.5 : sumMax / denom;

    result.push({
      time: candles[i].time,
      value: Number(dem.toFixed(4)),
    });
  }

  return result;
}
